import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { JOB_STATUS_BADGE_STYLE } from '@/components/shared/jobStatusColors'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { portalQuoteStatus, type PortalQuoteStatus } from '@/lib/quotes'
import {
  JOB_STATUS_LABELS,
  JOB_PRIORITY_LABELS,
  INVOICE_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  CONTRACT_STATUS_LABELS,
  TASK_STATUS_LABELS,
  TASK_PRIORITY_LABELS,
  TECHNICIAN_STATUS_LABELS,
  type JobStatus,
  type JobPriority,
  type QuoteStatus,
  type InvoiceStatus,
  type PaymentStatus,
  type ClientPaymentStatus,
  type ClientCategory,
  type ContractStatus,
  type TaskStatus,
  type TaskPriority,
} from '@/types'

function jobStatusLabel(status: JobStatus): string {
  return JOB_STATUS_LABELS[status].toLocaleUpperCase()
}

// Job status / priority badges follow the Figma agenda design system. Job
// status uses one shared light pill across every portal surface; priority is an
// outlined pill with a leading colour dot.
export function JobStatusBadge({
  status,
  label,
  className,
}: {
  status: JobStatus
  label?: string
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium leading-4',
        className
      )}
      style={JOB_STATUS_BADGE_STYLE[status]}
    >
      {label ?? jobStatusLabel(status)}
    </span>
  )
}

const PRIORITY_COLOR: Record<JobPriority, string> = {
  low: '#303030',
  medium: '#3f81ea',
  high: '#805c00',
  urgent: '#900b09',
}

export function JobPriorityBadge({
  priority,
  className,
}: {
  priority: JobPriority
  className?: string
}) {
  const color = PRIORITY_COLOR[priority]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border bg-background px-2 py-0.5 text-xs font-medium',
        className
      )}
      style={{ borderColor: color, color }}
    >
      <span
        className='h-1.5 w-1.5 rounded-full'
        style={{ backgroundColor: color }}
      />
      {JOB_PRIORITY_LABELS[priority]}
    </span>
  )
}

// Credit status (2.4.1-029 / 2.4.6-019).
// - outline: list tables — bordered pill + colour dot
// - solid: detail sidebar (Figma 2242:64107) — filled SDS Secondary Tag
export function CreditStatusBadge({
  status,
  variant = 'outline',
}: {
  status?: string | null
  variant?: 'outline' | 'solid'
}) {
  const { t } = useTranslation('shell')
  const restricted = (status ?? '').toLowerCase() === 'restricted'
  const label = restricted ? t('status.restricted') : t('status.normal')

  if (variant === 'solid') {
    return (
      <span
        className={
          restricted
            ? 'inline-flex h-[22px] items-center justify-center rounded-lg bg-[#fdd3d0] px-1.5 py-1 text-xs font-medium leading-4 text-[#900b09]'
            : 'inline-flex h-[22px] items-center justify-center rounded-lg bg-[#cff7d3] px-1.5 py-1 text-xs font-medium leading-4 text-[#02542d]'
        }
      >
        {label}
      </span>
    )
  }

  return (
    <span
      className={
        restricted
          ? 'inline-flex items-center gap-1.5 rounded-lg border border-destructive px-1.5 py-1 text-xs font-medium leading-4 text-destructive'
          : 'inline-flex items-center gap-1.5 rounded-lg border border-[#02542d] px-1.5 py-1 text-xs font-medium leading-4 text-[#02542d]'
      }
    >
      <span
        className={`size-1.5 shrink-0 rounded-full ${restricted ? 'bg-destructive' : 'bg-[#02542d]'}`}
      />
      {label}
    </span>
  )
}

const QUOTE_STATUS_TAG: Record<
  PortalQuoteStatus,
  { bg: string; text: string }
> = {
  draft: { bg: '#e7e9ef', text: '#3d434e' },
  sent: { bg: '#dbeafe', text: '#1d4ed8' },
  won: { bg: '#d1fae5', text: '#065f46' },
  lost: { bg: '#fecaca', text: '#991b1b' },
}

export function QuoteStatusBadge({
  status,
  className,
}: {
  status: QuoteStatus
  className?: string
}) {
  const { t } = useTranslation('quotes')
  const portalStatus = portalQuoteStatus(status)
  const tag = QUOTE_STATUS_TAG[portalStatus]
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium leading-4',
        className
      )}
      style={{ backgroundColor: tag.bg, color: tag.text }}
    >
      {t(`list.status.${portalStatus}`)}
    </span>
  )
}

// Quote source tag (Figma 987:35246): brand-tertiary pill for operator / technician.
export function QuoteSourceBadge({
  value,
  label,
}: {
  value?: string | null
  label?: string
}) {
  if (!value)
    return <span className='text-sm leading-[1.4] text-foreground'>-</span>
  const display =
    label && label.trim()
      ? label
      : value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ')
  return (
    <span className='inline-flex h-[22px] items-center justify-center rounded-lg bg-[#ffece6] px-1.5 py-1 text-xs font-medium leading-4 text-[#ff5a1f]'>
      {display}
    </span>
  )
}

// Invoice status tags (Figma 1085:12538): 12px medium text, 16px line-height,
// rounded-8, and per-status SDS semantic colours.
const INVOICE_STATUS_TAG: Record<string, { bg: string; text: string }> = {
  pro_forma: { bg: '#fbfbfc', text: '#171a20' },
  pro_forma_sent: { bg: '#efdbff', text: '#531dab' },
  validated: { bg: '#dbeafe', text: '#1d44b3' },
  pending: { bg: '#fef3c7', text: '#8a5405' },
  paid: { bg: '#d1fae5', text: '#065f46' },
  partially_paid: { bg: '#ffe2d2', text: '#ff5a1f' },
  overdue: { bg: '#fef3c7', text: '#8a5405' },
  overdue_5: { bg: '#fef3c7', text: '#8a5405' },
  overdue_21: { bg: '#fecaca', text: '#7f1d1d' },
  overdue_56: { bg: '#d63b33', text: '#ffffff' },
  cancelled_via_credit_note: { bg: '#f4f5f8', text: '#171a20' },
}

const DEFAULT_INVOICE_STATUS_TAG = { bg: '#f4f5f8', text: '#171a20' }

const OVERDUE_STATUS_TAGS = {
  low: { bg: '#fef3c7', text: '#8a5405' },
  medium: { bg: '#fecaca', text: '#7f1d1d' },
  high: { bg: '#d63b33', text: '#ffffff' },
} as const

function invoiceStatusLabel(status: InvoiceStatus | string, t: TFunction) {
  if (status in INVOICE_STATUS_LABELS) {
    return t(`invoiceStatus.${status}`)
  }

  return status.replace(/_/g, ' ')
}

function daysPastDue(dueDate?: string | null) {
  if (!dueDate) return null
  const [year, month, day] = dueDate.split('-').map(Number)
  if (!year || !month || !day) return null

  const due = new Date(year, month - 1, day)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const diff = today.getTime() - due.getTime()
  if (diff <= 0) return null
  return Math.ceil(diff / 86_400_000)
}

function overdueTag(days: number | null) {
  if (days === null || days <= 15) return OVERDUE_STATUS_TAGS.low
  if (days <= 30) return OVERDUE_STATUS_TAGS.medium
  return OVERDUE_STATUS_TAGS.high
}

export function InvoiceStatusBadge({
  status,
  dueDate,
  className,
}: {
  status: InvoiceStatus | string
  dueDate?: string | null
  className?: string
}) {
  const { t, i18n } = useTranslation('enums')
  const isOverdue = status.startsWith('overdue')
  const overdueDays = isOverdue ? daysPastDue(dueDate) : null
  const tag = isOverdue
    ? overdueTag(overdueDays)
    : (INVOICE_STATUS_TAG[status] ?? DEFAULT_INVOICE_STATUS_TAG)
  const label =
    isOverdue && overdueDays !== null
      ? i18n.language.startsWith('fr')
        ? `${overdueDays >= 31 ? 'Impayé' : 'Retard'} · ${overdueDays} j`
        : `Overdue · ${overdueDays} ${overdueDays === 1 ? 'day' : 'days'}`
      : invoiceStatusLabel(status, t)

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium leading-4',
        className
      )}
      style={{ backgroundColor: tag.bg, color: tag.text }}
    >
      {label}
    </span>
  )
}

// Credit note status badge (2.6.9-012): Issued / Sent.
export function CreditNoteStatusBadge({ status }: { status?: string | null }) {
  const { t } = useTranslation('shell')
  const s = (status ?? 'issued').toLowerCase()
  const label = s === 'sent' ? t('status.sent') : t('status.issued')
  return <Badge variant={s === 'sent' ? 'success' : 'info'}>{label}</Badge>
}

// Generic capitalised pill for client status / source. `value` drives the
// colour variant (raw enum); pass an optional already-translated `label` for
// the displayed text (falls back to a humanised form of `value`).
export function GenericStatusBadge({
  value,
  label,
}: {
  value?: string | null
  label?: string
}) {
  if (!value) return <span className='text-muted-foreground'>-</span>
  const display =
    label && label.trim()
      ? label
      : value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ')
  const active = value.toLowerCase() === 'active'
  return <Badge variant={active ? 'success' : 'secondary'}>{display}</Badge>
}

// Client type tags (Figma 2881:33987): Individual blue / Business purple /
// Property Manager amber.
const CLIENT_TYPE_TAG: Record<string, { bg: string; text: string }> = {
  individual: { bg: '#dbeafe', text: '#1d44b3' },
  company: { bg: '#efdbff', text: '#531dab' },
  property_management: { bg: '#fef3c7', text: '#8a5405' },
}

export function ClientTypeTagBadge({
  type,
  label,
}: {
  type?: string | null
  label: string
}) {
  const tag = CLIENT_TYPE_TAG[(type ?? '').toLowerCase()] ?? {
    bg: '#f4f5f8',
    text: '#171a20',
  }
  return (
    <span
      className='inline-flex min-h-[22px] items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4'
      style={{ backgroundColor: tag.bg, color: tag.text }}
    >
      {label}
    </span>
  )
}

// Client Active / Inactive tags (Figma 2881:33987).
export function ClientStatusTagBadge({
  status,
  label,
}: {
  status?: string | null
  label: string
}) {
  const active = (status ?? 'active').toLowerCase() === 'active'
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium whitespace-nowrap',
        active
          ? 'bg-[#d1fae5] leading-4 text-[#065f46]'
          : 'bg-[#f4f5f8] leading-[1.4] text-[#171a20]'
      )}
    >
      {label}
    </span>
  )
}

function isProspect(category?: ClientCategory | string | null): boolean {
  return (category ?? '').toLowerCase() === 'prospect'
}

/** Figma 2881:33989 — "P" chip beside a prospect client name. */
export function ProspectBadge({
  category,
}: {
  category?: ClientCategory | string | null
}) {
  if (!isProspect(category)) {
    return null
  }

  return (
    <span
      aria-label='Prospect'
      className='inline-flex h-5 shrink-0 items-center justify-center rounded-md px-1.5 py-0.5 text-xs font-medium leading-4 text-[#ff5a1f]'
      style={{ backgroundColor: '#fff1ea' }}
    >
      P
    </span>
  )
}

export function ClientNameWithProspect({
  name,
  category,
  className,
}: {
  name: string
  category?: ClientCategory | string | null
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex w-full min-w-0 max-w-full items-center gap-1',
        className
      )}
    >
      <TruncatedTextTooltip className='line-clamp-2' content={name}>
        {name}
      </TruncatedTextTooltip>
      <ProspectBadge category={category} />
    </span>
  )
}

// Client-list Payment Status (Figma 2881:33987): Normal / Pending / Late.
const CLIENT_PAYMENT_STATUS_TAG: Record<
  ClientPaymentStatus,
  { bg: string; text: string }
> = {
  normal: { bg: '#d1fae5', text: '#065f46' },
  pending: { bg: '#fef3c7', text: '#8a5405' },
  late: { bg: '#fecaca', text: '#7f1d1d' },
}

export function ClientPaymentStatusBadge({
  status,
  label,
}: {
  status?: string | null
  label: string
}) {
  const key = (status ?? 'normal').toLowerCase() as ClientPaymentStatus
  const tag = CLIENT_PAYMENT_STATUS_TAG[key] ?? CLIENT_PAYMENT_STATUS_TAG.normal
  return (
    <span
      className='inline-flex h-[22px] items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4 whitespace-nowrap'
      style={{ backgroundColor: tag.bg, color: tag.text }}
    >
      {label}
    </span>
  )
}

// Payment status badge (Figma 61:8408 / 61:8409 / 61:8410 / 61:8414):
// 24px-high rounded-8 pill, 12px semibold text, status-specific fills.
const PAYMENT_STATUS_TAG: Record<PaymentStatus, { bg: string; text: string }> =
  {
    pending: { bg: '#FEF3C7', text: '#8A5405' },
    paid: { bg: '#D1FAE5', text: '#065F46' },
    failed: { bg: '#FECACA', text: '#7F1D1D' },
    refunded: { bg: '#F4F5F8', text: '#171A20' },
  }

export function PaymentStatusBadge({
  status,
  className,
}: {
  status: PaymentStatus
  className?: string
}) {
  const tag = PAYMENT_STATUS_TAG[status]

  return (
    <Badge
      className={cn(
        'inline-flex h-6 items-center justify-center rounded-lg px-1.5 py-1 text-xs font-medium leading-4 whitespace-nowrap',
        className
      )}
      style={{ backgroundColor: tag.bg, color: tag.text }}
    >
      {PAYMENT_STATUS_LABELS[status]}
    </Badge>
  )
}

// Contract status tags (Figma 3396:160512): rounded-8 pill, 12px semibold.
const CONTRACT_STATUS_TAG: Record<
  ContractStatus,
  { bg: string; text: string }
> = {
  draft: { bg: '#f4f5f8', text: '#171a20' },
  pending_signature: { bg: '#dbeafe', text: '#1d44b3' },
  signed: { bg: '#d1fae5', text: '#065f46' },
  declined: { bg: '#fecaca', text: '#7f1d1d' },
  active: { bg: '#d1fae5', text: '#065f46' },
  to_be_renewed: { bg: '#fef3c7', text: '#8a5405' },
  suspended: { bg: '#ffe7ba', text: '#d46b08' },
  expired: { bg: '#f4f5f8', text: '#171a20' },
  terminated: { bg: '#fecaca', text: '#7f1d1d' },
  renewed: { bg: '#dbeafe', text: '#1d44b3' },
}

const DEFAULT_CONTRACT_STATUS_TAG = { bg: '#f4f5f8', text: '#171a20' }

function contractStatusLabel(
  status: ContractStatus | string | null | undefined
) {
  if (!status) return 'Default'
  if (status in CONTRACT_STATUS_LABELS) {
    return CONTRACT_STATUS_LABELS[status as ContractStatus]
  }
  return status.replace(/_/g, ' ')
}

export function ContractStatusBadge({
  status,
  label,
  size = 'sm',
  className,
}: {
  status: ContractStatus | string | null | undefined
  label?: string
  size?: 'sm' | 'md'
  className?: string
}) {
  const tag =
    status && status in CONTRACT_STATUS_TAG
      ? CONTRACT_STATUS_TAG[status as ContractStatus]
      : DEFAULT_CONTRACT_STATUS_TAG
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center justify-center rounded-lg font-medium',
        size === 'md'
          ? 'h-8 px-2 py-2 text-base leading-[1.4]'
          : 'px-1.5 py-1 text-xs leading-4',
        className
      )}
      style={{ backgroundColor: tag.bg, color: tag.text }}
    >
      {label ?? contractStatusLabel(status)}
    </span>
  )
}

// Task status "Tag" for task surfaces: light tinted chip for active states and
// a neutral grey chip for cancelled, matching the task list/detail Figma.
const TASK_STATUS_TAG: Record<TaskStatus, { bg: string; text: string }> = {
  todo: { bg: '#dbeafe', text: '#1d44b3' },
  in_progress: { bg: '#fef3c7', text: '#8a5405' },
  done: { bg: '#d1fae5', text: '#065f46' },
  cancelled: { bg: '#e7e9ef', text: '#171a20' },
}

export function TaskStatusBadge({ status }: { status: TaskStatus }) {
  const tag = TASK_STATUS_TAG[status]
  return (
    <span
      className='inline-flex items-center justify-center gap-1 rounded-lg px-1.5 py-1 text-center text-xs font-medium leading-4'
      style={{ backgroundColor: tag.bg, color: tag.text }}
    >
      {TASK_STATUS_LABELS[status]}
    </span>
  )
}

// Task priority badge for task list/detail: neutral outlined pill with a
// coloured dot. Unlike job priority badges, the text stays neutral.
const TASK_PRIORITY_COLOR: Record<TaskPriority, string> = {
  low: '#303030',
  medium: '#3f81ea',
  high: '#efac00',
  urgent: '#d63b33',
}

export function TaskPriorityBadge({ priority }: { priority: TaskPriority }) {
  const color = TASK_PRIORITY_COLOR[priority]
  return (
    <span className='inline-flex h-5 w-fit max-w-full shrink-0 items-center gap-1 rounded-md border border-[#d9d9d9] bg-white px-1.5 py-0.5 text-xs font-medium leading-4 text-[#272c36]'>
      <span
        className='size-1.5 shrink-0 rounded-full'
        style={{ backgroundColor: color }}
      />
      {TASK_PRIORITY_LABELS[priority]}
    </span>
  )
}

// Technician status badge (2.10.1-027): match the Figma pill colours used
// across the list/detail/availability surfaces.
const TECHNICIAN_STATUS_STYLES: Record<
  string,
  { backgroundColor: string; color: string }
> = {
  active: { backgroundColor: '#dcfce7', color: '#065f46' },
  inactive: { backgroundColor: '#f4f5f8', color: '#3d434e' },
  on_leave: { backgroundColor: '#fef3c7', color: '#b7791f' },
  on_intervention: { backgroundColor: '#dbeafe', color: '#356ae6' },
}

export function TechnicianStatusBadge({ status }: { status: string }) {
  const key = status in TECHNICIAN_STATUS_STYLES ? status : 'inactive'
  return (
    <Badge
      variant='outline'
      className='h-6 rounded-lg border-transparent bg-transparent px-1.5 py-1 text-xs font-medium leading-4'
      style={TECHNICIAN_STATUS_STYLES[key]}
    >
      {TECHNICIAN_STATUS_LABELS[key] ?? status}
    </Badge>
  )
}
